import { ChangeEvent, FC, useLayoutEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Dropdown,
	ErrorScreen,
	ImageUpload,
	Input,
	Loading,
	Message,
} from '../../../../components';
import { Option } from '../../../../components/dropdown/Dropdown';
import {
	getAllGroupDealers,
	getDealerById,
	postAdminDealer,
	putAdminDealer,
	TConcessionaria,
	TGrupoConcessionaria,
} from '../../../../services';
import { useToastStore } from '../../../../stores';
import { getImageUrl, opcoesDeSimOuNao, opcoesDeSimOuNaoPendente } from '../../../../utils';
import { Field, Form, Label } from '../../Registration.styles';
import { RegistrationFormPage } from '../../templates';
import { newDealershipFormInitialState, newDealershipFormReducer } from './newDealership.form';

const NewDealership: FC = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [gruposConcessionarias, setGruposConcessionarias] = useState<Option[]>([]);
	const [logotipoToUpload, setLogotipoToUpload] = useState<File | null>(null);
	const [logotipoOnServer, setLogotipoOnSever] = useState<string | undefined>(undefined);
	const [state, dispatch] = useReducer(newDealershipFormReducer, newDealershipFormInitialState);
	const statusAtivoSelecionado = useMemo(
		() => opcoesDeSimOuNao.find((status) => status.value === state.tagAtivo),
		[state.tagAtivo]
	);
	const statusDeferimentoSelecionado = useMemo(
		() => opcoesDeSimOuNaoPendente.find((status) => status.value === state.tagDeferimento),
		[state.tagDeferimento]
	);
	const grupoSelecionado = useMemo(
		() => gruposConcessionarias.find((grupo) => grupo.value === state.idGrupoConcessionaria),
		[state.idGrupoConcessionaria, gruposConcessionarias]
	);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
	};

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
	};

	const handleSubmit = async () => {
		try {
			const requestBody = {
				...state,
				prioridade_minuto: state.prioridade_minuto !== null && state.prioridade_minuto !== undefined
					? Number(state.prioridade_minuto)
					: null,
			};

			if (id) {
				const response = await putAdminDealer(requestBody, id, logotipoToUpload);

				if (!response.success) {
					setToast({ message: response.message, variant: 'error' });
					return;
				}

				setToast({ message: response.message, variant: 'success' });
			} else {
				const response = await postAdminDealer(requestBody, logotipoToUpload);

				if (!response.success) {
					setToast({ message: response.message, variant: 'error' });
					return;
				}

				setToast({ message: response.message, variant: 'success' });
			}

			navigate('/cadastros/concessionarias');
		} catch {
			const errorMessage = id
				? 'Erro ao atualizar concessionária. Tente novamente.'
				: 'Erro ao criar concessionária. Tente novamente.';

			setToast({ message: errorMessage, variant: 'error' });
		}
	};

	useLayoutEffect(() => {
		const fetchDealerGroups = async () => {
			setLoading(true);

			try {
				const response = await getAllGroupDealers();

				if (!response) {
					throw new Error();
				}

				const opcoesDeGrupo = response.map((grupo) => ({
					value: grupo.id,
					label: grupo.grupoConcessionaria,
				}));
				opcoesDeGrupo.sort((a, b) => a.label.localeCompare(b.label));

				setGruposConcessionarias(opcoesDeGrupo);
			} catch {
				setError(true);
				setToast({ message: 'Erro ao buscar grupos de concessionária', variant: 'error' });
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		const verificarImagem = async (url: string) => {
			return new Promise<boolean>((resolve) => {
				const img = new Image(); 
				img.onload = () => resolve(true); 
				img.onerror = () => resolve(false); 
				img.src = url; 
			});
		};
		
		const fetchDealer = async () => {
			if (!id) return;
		
			try {
				const response = await getDealerById({ id });
		
				if (!response) {
					throw new Error();
				}
		
				const initialFormState = {
					idGrupoConcessionaria: response.grupoConcessionaria.id,
					razaoSocial: response.razaoSocial || '',
					fantasia: response.fantasia || '',
					cnpj: response.cnpj || '',
					inscricaoEstadual: response.inscricaoEstadual || '',
					inscricaoMunicipal: response.inscricaoMunicipal || '',
					email: response.email || '',
					telefone: response.telefone || '',
					celular: response.celular || '',
					cep: response.cep || '',
					estado: response.estado || '',
					cidade: response.cidade || '',
					bairro: response.bairro || '',
					logradouro: response.logradouro || '',
					numeroLogradouro: response.numeroLogradouro || '',
					complementoLogradouro: response.complementoLogradouro || '',
					responsavel: response.responsavel || '',
					indicacao: response.indicacao || '',
				tagAtivo: response.tagAtivo || '',
				tagDeferimento: response.tagDeferimento || '',
				observacao: response.observacao || '',
				prioridade_minuto: response.prioridade_minuto ?? null,
			};
		
				const grupoConcessionariaUrl = getImageUrl.grupoConcessionaria({
					id: response?.id,
					logotipo: response.logotipo,
				} as TGrupoConcessionaria);
		
				const concessionariaUrl = getImageUrl.concessionaria({
					id: Number(id),
					logotipo: response.logotipo,
				} as TConcessionaria);
		
				let finalImageUrl = `${process.env.PUBLIC_URL}/images/sem_foto.png`; 
		
				const imageExistsInConcessionaria = await verificarImagem(concessionariaUrl);
				if (imageExistsInConcessionaria) {
					finalImageUrl = concessionariaUrl;  
				} else {
					const imageExistsInGrupoConcessionaria = await verificarImagem(grupoConcessionariaUrl);
					if (imageExistsInGrupoConcessionaria) {
						finalImageUrl = grupoConcessionariaUrl; 
					}
				}
		
				dispatch({ type: 'REPLACE_STATE', payload: initialFormState });
				setLogotipoOnSever(finalImageUrl);
		
			} catch (error) {
				setToast({ message: 'Erro ao buscar concessionária', variant: 'error' });
			}
		};
		
		fetchDealer();
		fetchDealerGroups();
		
		
	}, [id, setToast]);

	if (loading) return <Loading fullScreen />;

	if (error) return <ErrorScreen fullScreen />;

	return (
		<RegistrationFormPage title='Cadastrar Concessionária' onSubmit={handleSubmit}>
			<Form>
				<Label>Grupo de Concessionária</Label>
				<Field>
					<Dropdown
						options={gruposConcessionarias}
						selected={grupoSelecionado}
						onSelect={(option: Option) => handleSelectOption('idGrupoConcessionaria', option)}
					/>
				</Field>
				<Label>Razão Social</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Razão Social'
						value={state.razaoSocial}
						onChange={handleInputChange}
						name='razaoSocial'
					/>
				</Field>
				<Label>Nome Fantasia</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Nome Fantasia'
						value={state.fantasia}
						onChange={handleInputChange}
						name='fantasia'
					/>
				</Field>
				<Label>CNPJ</Label>
				<Field>
					<Input
						variant='text'
						placeholder='CNPJ'
						value={state.cnpj}
						onChange={handleInputChange}
						name='cnpj'
						maxLength={18}
					/>
				</Field>
				<Label>Inscrição Estadual</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Inscrição Estadual'
						value={state.inscricaoEstadual}
						onChange={handleInputChange}
						name='inscricaoEstadual'
					/>
				</Field>
				<Label>Inscrição Municipal</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Inscrição Municipal'
						value={state.inscricaoMunicipal}
						onChange={handleInputChange}
						name='inscricaoMunicipal'
					/>
				</Field>
				<Label>E-mail</Label>
				<Field>
					<Input
						variant='text'
						placeholder='E-mail'
						value={state.email}
						onChange={handleInputChange}
						name='email'
					/>
				</Field>
				<Label>Telefone</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Telefone'
						value={state.telefone}
						onChange={handleInputChange}
						name='telefone'
						maxLength={14}
					/>
				</Field>
				<Label>Celular</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Celular'
						value={state.celular}
						onChange={handleInputChange}
						name='celular'
						maxLength={15}
					/>
				</Field>
				<Label>CEP</Label>
				<Field>
					<Input
						variant='text'
						placeholder='CEP'
						value={state.cep}
						onChange={handleInputChange}
						name='cep'
						maxLength={9}
					/>
				</Field>
				<Label>Logradouro</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Logradouro'
						value={state.logradouro}
						onChange={handleInputChange}
						name='logradouro'
					/>
				</Field>
				<Label>Número</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Número'
						value={state.numeroLogradouro}
						onChange={handleInputChange}
						name='numeroLogradouro'
					/>
				</Field>
				<Label>Complemento</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Complemento'
						value={state.complementoLogradouro}
						onChange={handleInputChange}
						name='complementoLogradouro'
					/>
				</Field>
				<Label>Bairro</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Bairro'
						value={state.bairro}
						onChange={handleInputChange}
						name='bairro'
					/>
				</Field>
				<Label>Cidade</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Cidade'
						value={state.cidade}
						onChange={handleInputChange}
						name='cidade'
					/>
				</Field>
				<Label>Estado</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Estado'
						value={state.estado}
						onChange={handleInputChange}
						name='estado'
					/>
				</Field>
				<Label>Responsável</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Responsável'
						value={state.responsavel}
						onChange={handleInputChange}
						name='responsavel'
					/>
				</Field>
				<Label>Indicação</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Indicação'
						value={state.indicacao}
						onChange={handleInputChange}
						name='indicacao'
					/>
				</Field>
				<Label>Ativo?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNao}
						selected={statusAtivoSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagAtivo', option)}
					/>
				</Field>
				<Label>Deferido?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNaoPendente}
						selected={statusDeferimentoSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagDeferimento', option)}
					/>
				</Field>
				<Label>Observação</Label>
				<Field>
					<Message
						placeholder='Observação'
						value={state.observacao}
						onChange={handleInputChange}
						name='observacao'
					/>
				</Field>
				<Label>Prioridade (minutos)</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Ex: 30'
						value={state.prioridade_minuto !== null && state.prioridade_minuto !== undefined ? String(state.prioridade_minuto) : ''}
						onChange={(e) => {
							const raw = e.target.value.replace(/\D/g, '');
							dispatch({
								type: 'SET_SELECT_OPTION',
								field: 'prioridade_minuto',
								value: raw === '' ? null as unknown as number : Number(raw),
							});
						}}
						name='prioridade_minuto'
					/>
				</Field>
				<Label>Logotipo</Label>
				<Field>
					<ImageUpload
						imageOnServer={logotipoOnServer}
						imageToUpload={logotipoToUpload}
						setImageToUpload={setLogotipoToUpload}
					/>
				</Field>
			</Form>
		</RegistrationFormPage>
	);
};

export default NewDealership;
